import { DialogActions } from '@mui/material'

import {
  ConfigurableProperty,
  DigitalTwin,
  DigitalTwinSettingCreationRequest,
  getSavedSetting,
  TimeSeriesSettingCreationRequest,
  useCreateDigitalTwinSetting,
  useCreateTimeSeriesSetting,
  useDeleteDeviationSetting,
  useDeleteTimeSeriesSetting,
  useUpdateDigitalTwinSetting,
  useUpdateTimeSeriesSetting,
} from 'api/digitalTwin/digitalTwin.api'
import { classifySettingSaveResult, classifySettingSaveResults } from 'api/digitalTwin/digitalTwin.error.helper'
import type {
  DigitalTwinSetting,
  DigitalTwinSettingCreationResult,
  TimeSeriesSettingsCreationResult,
} from 'api/digitalTwin/digitalTwin.types'
import { getValueHelperTextForSetting } from 'helpers/optimizeSettings.helper/optimizeSettings.helper'
import React, { ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import ModalButtons from 'ui/molecules/ModalButtons/ModalButtons'
import { DiscardCopyDialog } from '../DigitalTwinSettingEditor/DiscardCopyDialog'
import { CopySettingAction } from './DigitalTwinSettingDialog.helper'
import { CreateSettingData } from './DigitalTwinSettingDialogContent'

type DigitalTwinSettingDialogActionsProps = {
  digitalTwin: DigitalTwin
  initData: null | CreateSettingData
  newSetting: DigitalTwinSettingCreationRequest
  activeConfigurableProperty?: ConfigurableProperty
  disable?: boolean
  disableOnSave?: boolean
  disableOnCopy?: boolean
  bypassDisabledOnSubmitForExcludeLayerSetting?: boolean
  submitLabel?: string
  /** Whether the form differs from the setting as it opened; an existing setting can only be saved once it does. */
  hasUnsavedChanges: boolean
  hasSavableChanges: boolean
  onClose: () => void
  onRequestClose: () => void
  onSettingModified?: () => void
  /** Replaces `onClose` after a successful save; `savedSetting` is null when the save stored several settings. */
  onSaveSuccess?: (submittedSetting: DigitalTwinSettingCreationRequest, savedSetting: DigitalTwinSetting | null) => void
  onInvalidIndexChange: (invalidValuesIndexes: number[], valueHelperText: string) => void
  onCopy?: () => void
  copyActions?: CopySettingAction[]
  onBackToOriginal?: () => void
  setSettingInstructionsData?: (data: DigitalTwinSettingCreationRequest | { id?: number; delete?: boolean }) => void
}

export default function DigitalTwinSettingDialogActions({
  digitalTwin,
  newSetting,
  activeConfigurableProperty,
  initData,
  disable = false,
  disableOnSave = false,
  disableOnCopy = false,
  bypassDisabledOnSubmitForExcludeLayerSetting = false,
  submitLabel,
  hasUnsavedChanges,
  hasSavableChanges,
  onClose,
  onRequestClose,
  onSettingModified,
  onSaveSuccess,
  onInvalidIndexChange,
  onCopy,
  copyActions,
  onBackToOriginal,
  setSettingInstructionsData,
}: DigitalTwinSettingDialogActionsProps): ReactElement {
  const { t } = useTranslation()

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [discardCopyConfirmOpen, setDiscardCopyConfirmOpen] = useState(false)

  const { mutateAsync: createSetting, isPending: isCreatingSettingLoading } = useCreateDigitalTwinSetting()
  const { mutateAsync: createTimeSeriesSetting, isPending: isCreatingTimeSeriesSettingLoading } =
    useCreateTimeSeriesSetting()
  const { mutateAsync: updateDeviationSetting, isPending: isUpdatingDeviationSettingLoading } =
    useUpdateDigitalTwinSetting()
  const { mutateAsync: updateTimeSeriesSetting, isPending: isUpdatingTimeSeriesSettingLoading } =
    useUpdateTimeSeriesSetting()
  const { mutateAsync: deleteDeviationSetting } = useDeleteDeviationSetting()
  const { mutateAsync: deleteTimeSeriesSetting } = useDeleteTimeSeriesSetting()

  const hasWriteFalseProperty = activeConfigurableProperty?.write === false

  const maxValue = activeConfigurableProperty?.max_value
  const minValue = activeConfigurableProperty?.min_value

  const settingStartTime = newSetting.start_time
  const settingEndTime = newSetting.end_time

  const onSubmit = useCallback(() => {
    if (settingStartTime && settingEndTime && settingStartTime > settingEndTime) {
      return // Early return because of bad setting time period
    }

    const { errorMessage: valueHelperText, invalidValuesIndexes: newInvalidValuesIndexes } =
      getValueHelperTextForSetting(newSetting, minValue, maxValue, t)
    if (valueHelperText) {
      onInvalidIndexChange(newInvalidValuesIndexes, valueHelperText)
      return // Early return because of bad setting value
    }

    // Setting is valid. Save as time series or regular setting, then show a success message and close the dialog.
    let modificationPromise: Promise<
      DigitalTwinSettingCreationResult | TimeSeriesSettingsCreationResult | TimeSeriesSettingsCreationResult[]
    >
    const createSettingInstructionsInsteadOfSettings = !!setSettingInstructionsData
    if (!createSettingInstructionsInsteadOfSettings) {
      if (newSetting.isTimeSeries) {
        modificationPromise =
          initData?.id !== undefined
            ? updateTimeSeriesSetting({
                overwrite: false,
                settingId: initData.id,
                data: newSetting,
                digitalTwinUid: digitalTwin.uid,
              })
            : createTimeSeriesSetting({
                digitalTwinUid: digitalTwin.uid,
                data: newSetting as TimeSeriesSettingCreationRequest,
                splitOnNullValues: true,
              })
      } else {
        modificationPromise =
          initData?.id !== undefined
            ? updateDeviationSetting({
                overwrite: true,
                settingId: initData.id,
                data: newSetting,
              })
            : createSetting({
                overwrite: true,
                digitalTwinUid: digitalTwin.uid,
                data: newSetting,
              })
      }

      modificationPromise.then(
        (data) => {
          const outcome = Array.isArray(data) ? classifySettingSaveResults(data) : classifySettingSaveResult(data)
          if (outcome.kind === 'saved') {
            onSettingModified && onSettingModified()
            if (onSaveSuccess) {
              onSaveSuccess(newSetting, getSavedSetting(data))
            } else {
              onClose()
            }
          }
        },
        () => undefined
      )
    } else if (createSettingInstructionsInsteadOfSettings) {
      //update and save new setting instructions
      if (initData?.id !== undefined && initData.id !== null) {
        //update send with id
        setSettingInstructionsData({ ...newSetting, id: initData?.id })
      } else {
        // new setting
        setSettingInstructionsData({ ...newSetting })
      }
      onClose()
    }
  }, [
    settingStartTime,
    settingEndTime,
    newSetting,
    minValue,
    maxValue,
    t,
    onInvalidIndexChange,
    setSettingInstructionsData,
    initData?.id,
    updateTimeSeriesSetting,
    digitalTwin.uid,
    createTimeSeriesSetting,
    updateDeviationSetting,
    createSetting,
    onSettingModified,
    onSaveSuccess,
    onClose,
  ])

  if (!digitalTwin || !initData) {
    return <></>
  }

  const isNewOrChanged = initData.id === undefined || hasUnsavedChanges

  const isSubmitting =
    isCreatingSettingLoading ||
    isCreatingTimeSeriesSettingLoading ||
    isUpdatingDeviationSettingLoading ||
    isUpdatingTimeSeriesSettingLoading

  const startAfterEnd = !!(settingStartTime && settingEndTime && settingStartTime > settingEndTime)

  const isDisabledForSubmit = disableOnSave || (disable && !bypassDisabledOnSubmitForExcludeLayerSetting)
  // An existing setting stored without a value may be saved as it is; a new setting or a setting instruction needs one.
  const lacksValue =
    newSetting.value === undefined ||
    (newSetting.value === null && (initData.id === undefined || !!setSettingInstructionsData))
  const disableSubmit =
    !settingStartTime ||
    !newSetting.comment ||
    lacksValue ||
    startAfterEnd ||
    hasWriteFalseProperty ||
    isDisabledForSubmit ||
    !isNewOrChanged

  const handleBackToOriginal = (): void => (hasSavableChanges ? setDiscardCopyConfirmOpen(true) : onBackToOriginal?.())

  const onDeleteFunction =
    initData?.id !== undefined || setSettingInstructionsData ? () => setDeleteConfirmOpen(true) : undefined

  return (
    <>
      <DialogActions>
        <ModalButtons
          submitButtonProps={{ 'data-testid': 'setting-save-button' }} // e2e: inner setting save button — do not remove
          onDelete={onDeleteFunction}
          disableSubmit={disableSubmit}
          disableDelete={disable || hasWriteFalseProperty}
          disableCopy={disableOnCopy}
          submitLabel={submitLabel}
          copyTooltipText={
            initData?.operational_event
              ? t('The copied setting will be saved as an independent setting, not linked to any event')
              : undefined
          }
          isSubmitting={isSubmitting}
          onSubmit={onSubmit}
          onCancel={onRequestClose}
          onCopy={onCopy}
          copyActions={copyActions}
          onBackToOriginal={onBackToOriginal && handleBackToOriginal}
        />
      </DialogActions>
      <DiscardCopyDialog
        open={discardCopyConfirmOpen}
        onClose={() => setDiscardCopyConfirmOpen(false)}
        onConfirm={() => {
          setDiscardCopyConfirmOpen(false)
          onBackToOriginal?.()
        }}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
        }}
        onConfirm={() => {
          if (setSettingInstructionsData) {
            setSettingInstructionsData({ ...newSetting, id: initData.id, delete: true })
          } else if (initData?.id !== undefined) {
            const deletePromise = newSetting.isTimeSeries
              ? deleteTimeSeriesSetting({ settingId: initData.id, digitalTwinUid: digitalTwin.uid })
              : deleteDeviationSetting({ settingId: initData.id })
            deletePromise.then(() => {
              onSettingModified && onSettingModified()
            })
          }
          setDeleteConfirmOpen(false)
          onClose()
        }}
        textObject={{
          title: t(`Do you really want to delete this setting?`),
          text: t(
            `If you remove this setting it will be deleted from history and not included in the follow-up. Instead, you could choose to cancel and then edit the end time.`
          ),
        }}
      />
    </>
  )
}
